import { useCallback, useEffect, useState } from "react";
import { useManagerScope } from "@/components/ManagerLayout";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Users, CalendarClock, AlertTriangle, TrendingUp, TrendingDown, Clock,
  ChevronRight, CalendarDays, ClipboardList, Wifi,
} from "lucide-react";
import { MANAGER_SITE_LABEL } from "@/components/ManagerLayout";

function fmtTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}
function fmtCurrency(n) {
  return (n || 0).toLocaleString("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 0 });
}
function fmtDateShort(iso) {
  return new Date(iso + "T00:00:00").toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });
}

/** Pick the alert pill tone for a "variance" panel: roster-not-published
 *  ⇒ info; ≥1 incident ⇒ severity colour; otherwise neutral info. */
function alertTone(panel, severity) {
  if (panel?.status === "requires_roster") return "info";
  return panel?.count ? severity : "info";
}

/** Map a roster.status to a one-line subtitle for the dashboard's
 *  "Roster this week" tile. */
function rosterSubtitle(status) {
  if (status === "PUBLISHED") return "Published — staff can see it";
  if (status === "DRAFT") return "Draft — not yet visible to staff";
  return "Not yet created";
}

function Stat({ label, value, suffix, icon: Icon, tone = "navy", testid, to }) {
  const tones = {
    navy: "bg-[#1A2744] text-white",
    gold: "bg-[#C9A84C] text-[#1A2744]",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    ok: "bg-emerald-50 text-emerald-800 border-emerald-200",
  };
  const content = (
    <Card className={`border-0 shadow-sm ${tones[tone] || ""}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold opacity-80">{label}</div>
            <div className="font-heading text-3xl font-bold mt-2" data-testid={testid}>
              {value}
              {suffix && <span className="text-sm opacity-70 ml-1 font-medium">{suffix}</span>}
            </div>
          </div>
          {Icon && <Icon className="h-5 w-5 opacity-70" strokeWidth={1.8} />}
        </div>
      </CardContent>
    </Card>
  );
  return to ? <Link to={to} className="block hover:opacity-90 transition">{content}</Link> : content;
}

function AlertRow({ title, children, icon: Icon, tone = "amber", testid }) {
  const tones = {
    amber: "border-amber-200 bg-amber-50/50 text-amber-900",
    red: "border-red-200 bg-red-50/50 text-red-900",
    info: "border-gray-200 bg-gray-50/60 text-gray-700",
  };
  return (
    <div className={`rounded-xl border ${tones[tone]} p-4`} data-testid={testid}>
      <div className="flex items-start gap-3">
        {Icon && <Icon className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={1.8} />}
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold">{title}</div>
          <div className="text-xs mt-1 space-y-1">{children}</div>
        </div>
      </div>
    </div>
  );
}

export default function ManagerDashboard() {
  const [data, setData] = useState(null);
  const { scopeToken } = useManagerScope();

  const load = useCallback(() => {
    api.get("/manager/dashboard").then(({ data }) => setData(data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load, scopeToken]);

  if (!data) return <div className="text-sm text-gray-500">Loading dashboard…</div>;

  const labour = data.labour;
  const variance = labour.cost_variance;
  const varianceUp = variance > 0;

  return (
    <div className="space-y-6" data-testid="mgr-dashboard">
      <header>
        <p className="text-xs text-[#C9A84C] uppercase tracking-[0.25em] font-semibold">Today at {MANAGER_SITE_LABEL[data.site]}</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A2744] mt-1">Hey there. Here's the run-down.</h1>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Clocked in now" value={data.clocked_in.length} icon={Users} tone="navy" testid="stat-clocked-in" />
        <Stat label="Pending leave" value={data.counts.pending_leave} icon={CalendarClock} tone="gold" testid="stat-pending-leave" to="/admin/manager/leave" />
        <Stat label="Incomplete shifts" value={data.counts.incomplete_shifts} icon={AlertTriangle} tone="amber" testid="stat-incomplete" to="/admin/manager/timesheets?status=INCOMPLETE" />
        <Stat label="Site employees" value={data.counts.site_employees} icon={ClipboardList} tone="ok" testid="stat-employees" />
      </div>

      {/* Live clocked-in + Labour cost */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 border-gray-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-heading text-lg font-bold text-[#1A2744] flex items-center gap-2">
                  <Wifi className="h-4 w-4 text-[#C9A84C]" strokeWidth={1.8} />
                  Clocked in right now
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">Refreshes every 60s</p>
              </div>
              <Badge variant="outline" className="text-[11px] bg-emerald-50 text-emerald-800 border-emerald-200">
                {data.clocked_in.length} live
              </Badge>
            </div>
            {data.clocked_in.length === 0 ? (
              <p className="text-sm text-gray-500">No one is clocked in right now.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {data.clocked_in.map((c) => (
                  <li key={c.shift_id} className="py-2.5 flex items-center justify-between gap-3 text-sm" data-testid={`live-${c.user_id}`}>
                    <div className="min-w-0">
                      <div className="font-medium text-[#1A2744] truncate">{c.name}</div>
                      <div className="text-[11px] text-gray-500">Since {fmtTime(c.clock_in_ts)} · {c.employee_code}</div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="font-heading font-bold text-[#1A2744]">{c.hours_so_far.toFixed(2)}</div>
                      <div className="text-[10px] text-gray-400 uppercase tracking-widest">hrs so far</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="border-gray-200">
          <CardContent className="p-6">
            <h3 className="font-heading text-lg font-bold text-[#1A2744] flex items-center gap-2">
              {varianceUp ? <TrendingUp className="h-4 w-4 text-red-600" /> : <TrendingDown className="h-4 w-4 text-emerald-600" />}
              Labour cost · this week
            </h3>
            <div className="mt-4">
              <div className="font-heading text-4xl font-bold text-[#1A2744]" data-testid="labour-this">
                {fmtCurrency(labour.this_week_cost)}
              </div>
              <div className="text-xs text-gray-500 mt-1">{labour.this_week_hours.toFixed(1)} hrs this week</div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">Last week</span>
                <span className="font-medium text-[#1A2744]">{fmtCurrency(labour.last_week_cost)}</span>
              </div>
              <div className="flex items-center justify-between text-xs mt-1">
                <span className="text-gray-500">Variance</span>
                <span className={varianceUp ? "text-red-600 font-semibold" : "text-emerald-700 font-semibold"}>
                  {varianceUp ? "+" : ""}{fmtCurrency(variance)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts */}
      <Card className="border-gray-200">
        <CardContent className="p-6">
          <h3 className="font-heading text-lg font-bold text-[#1A2744] flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-[#C9A84C]" strokeWidth={1.8} /> Alerts
          </h3>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <AlertRow
              title={`Missed clock-out (${data.alerts.missed_clockout.count})`}
              icon={Clock}
              tone={data.alerts.missed_clockout.count ? "red" : "info"}
              testid="alert-missed-clockout"
            >
              {data.alerts.missed_clockout.items.length === 0 ? (
                <p className="text-gray-500">All shifts currently clocked out on time.</p>
              ) : (
                data.alerts.missed_clockout.items.map((i) => (
                  <div key={i.shift_id} className="text-xs">
                    <span className="font-medium">{i.name}</span> · in at {fmtTime(i.clock_in_ts)}
                  </div>
                ))
              )}
            </AlertRow>
            <AlertRow
              title={`No-show alerts (${data.alerts.no_show_alerts.count})`}
              icon={AlertTriangle}
              tone={alertTone(data.alerts.no_show_alerts, "red")}
              testid="alert-no-show"
            >
              {data.alerts.no_show_alerts.status === "requires_roster" ? (
                <p>Publish a roster for this week to activate this alert.</p>
              ) : data.alerts.no_show_alerts.items.length === 0 ? (
                <p className="text-gray-500">Everyone rostered is clocked in on time.</p>
              ) : (
                data.alerts.no_show_alerts.items.map((i) => (
                  <div key={i.shift_id} className="text-xs">
                    <span className="font-medium">{i.name}</span> · rostered {i.rostered_start} · {i.minutes_late}m late
                  </div>
                ))
              )}
            </AlertRow>
            <AlertRow
              title={`Early clock-in (${data.alerts.early_clock_in_alerts.count})`}
              icon={AlertTriangle}
              tone={alertTone(data.alerts.early_clock_in_alerts, "amber")}
              testid="alert-variance"
            >
              {data.alerts.early_clock_in_alerts.status === "requires_roster" ? (
                <p>Publish a roster for this week to catch &gt;20m early clock-ins.</p>
              ) : data.alerts.early_clock_in_alerts.items.length === 0 ? (
                <p className="text-gray-500">No early clock-ins today.</p>
              ) : (
                data.alerts.early_clock_in_alerts.items.map((i) => (
                  <div key={i.shift_id} className="text-xs">
                    <span className="font-medium">{i.name}</span> · {i.variance_minutes}m before {i.rostered_start}
                  </div>
                ))
              )}
            </AlertRow>
          </div>
        </CardContent>
      </Card>

      {/* Roster status + Upcoming holidays */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-gray-200 bg-gradient-to-br from-[#1A2744] to-[#263A63] text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-heading text-lg font-bold flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-[#C9A84C]" strokeWidth={1.8} /> Roster this week
                </h3>
                <p className="text-xs text-white/60 mt-1">
                  {rosterSubtitle(data.roster.status)}
                </p>
              </div>
            </div>
            <p className="text-sm text-white/80 mt-4">
              Build and publish this week's roster — drafts, copy last week, templates, publish notifications.
            </p>
            <Link to="/admin/manager/roster" className="inline-flex items-center gap-1 mt-4 text-xs text-[#C9A84C] hover:text-white" data-testid="link-roster">
              Open Roster Builder <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </CardContent>
        </Card>

        <Card className="border-gray-200">
          <CardContent className="p-6">
            <h3 className="font-heading text-lg font-bold text-[#1A2744] flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-[#C9A84C]" strokeWidth={1.8} /> Upcoming holidays
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">Next 30 days · {MANAGER_SITE_LABEL[data.site]} + national</p>
            <ul className="mt-4 divide-y divide-gray-100">
              {data.holidays.length === 0 && (
                <li className="py-2 text-sm text-gray-500">Nothing in the next 30 days.</li>
              )}
              {data.holidays.map((h) => (
                <li key={h.id} className="py-2 flex items-center justify-between gap-3 text-sm" data-testid={`holiday-${h.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-[#1A2744] truncate">{h.name}</div>
                    <div className="text-[11px] text-gray-500">
                      {fmtDateShort(h.date)}
                      {h.scope !== "NATIONAL" && <span className="ml-2 text-[#C9A84C]">· Regional</span>}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      <div className="flex gap-2 pt-2">
        <Link to="/admin/manager/leave" className="text-sm text-[#1A2744] hover:text-[#C9A84C] inline-flex items-center gap-1" data-testid="link-leave">
          Review leave requests <ChevronRight className="h-4 w-4" />
        </Link>
        <Link to="/admin/manager/timesheets" className="text-sm text-[#1A2744] hover:text-[#C9A84C] inline-flex items-center gap-1" data-testid="link-timesheets">
          Review timesheets <ChevronRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
